import { isMostlyPlcCode } from '@realplc/types';
import { GeneratedResult } from '../types';
import { looksLikePlcCode } from './messageTopic';
import { stripInternalScaffolding } from './reasoningDisplay';

const STRUCTURED_SECTION_NAMES = [
  'THOUGHT',
  'CODE',
  'EXPLANATION',
  'VERIFICATION',
  'CLARIFY',
  'FLOWCHART',
  'SUMMARY',
  'FOLLOWUP',
  'FAULTS',
  'QUESTIONS',
  'RISKS',
  'GLOBALS',
] as const;

const STRUCTURED_SECTION_SET = new Set<string>(STRUCTURED_SECTION_NAMES);
const structuredStartPattern = /\[\/?START_(THOUGHT|CODE|EXPLANATION|VERIFICATION|CLARIFY|FLOWCHART|SUMMARY|FOLLOWUP|FAULTS|QUESTIONS|RISKS|GLOBALS)\]/i;
const structuredTagPattern = /\[\/?(?:START|END)_[A-Z0-9_]+\]/gi;
/**
 * A structured marker the model wrapped in emphasis, so it reads as the bare one.
 *
 * The generation prompts ask for `[START_FAULTS]`; the model is writing markdown
 * and routinely bolds it. Stripping only the bracketed part left the asterisk
 * runs behind, and they reached the screen as literal `**` lines above and below
 * the fault table. The backreference is what keeps a section that opens with its
 * own `**扫描周期**` intact: only a run that wraps the marker and nothing else is
 * taken with it.
 */
const emphasisedMarkerPattern = /(\*{1,2})(\[\/?(?:START|END)_[A-Z0-9_]+\])\1/gi;
const bareStartEndPattern = /^\s*(START|END)[_\s-]*(THOUGHT|CODE|EXPLANATION|VERIFICATION|CLARIFY|FLOWCHART|FOLLOWUP|SUMMARY|SUMM?ARY|SUAMMARY)\s*:?\s*$/i;
const bareSectionPattern = /^\s*(THOUGHT|CODE|VERIFICATION|CLARIFY|FLOWCHART|FOLLOWUP|SUMMARY|SUMM?ARY|SUAMMARY)\s*:?\s*$/i;
const bareStartEndTokenPattern = /\b(?:START|END)[_\s-]*(THOUGHT|CODE|EXPLANATION|VERIFICATION|CLARIFY|FLOWCHART|FOLLOWUP|SUMMARY|SUMM?ARY|SUAMMARY)\b/gi;
const mermaidFenceStartPattern = /^(\s*)(`{3,}|~{3,})\s*mermaid\b[^\n\r]*(?:\r?\n|$)/gim;
const streamErrorBoundaryPattern = String.raw`\r?\n\s*\*\[Error\]\*`;

function toText(content: unknown): string {
  return typeof content === 'string' ? content : '';
}

function canonicalStructuredTag(value: string | undefined): string | null {
  const normalized = (value || '').replace(/[^A-Za-z]/g, '').toUpperCase();
  if (!normalized) return null;
  if (normalized === 'SUAMMARY' || normalized === 'SUMARY' || normalized === 'SUMMRY') {
    return 'SUMMARY';
  }
  return STRUCTURED_SECTION_SET.has(normalized) ? normalized : null;
}

/**
 * A `[START_CODE]` the model only mentions is not a section.
 *
 * Our own prompt says "Emit [START_CODE], the PLC program, then [END_CODE].
 * Stop there." and "Then output [START_CODE] with the PLC code. Stop after
 * [END_CODE]", and models say both back before they answer. Each recital is a
 * complete pair of markers around our own words, and every reader of section
 * shape believed it (production 2026-09-20): it ended the THOUGHT it was
 * written in, so the rest of the reasoning fell into the body; the body fenced
 * the words, so 生成代码 showed code cards titled "ST" holding `, the PLC
 * program, then`; and the parse kept them as the turn's program.
 *
 * Settled here because every one of those readers canonicalises first. The
 * markers go and the words stay, as the prose they are.
 *
 * What a span holds is judged by the shared grammar. One still arriving cannot
 * be yet — a program may open with a comment header — so it is judged by where
 * its marker stands: a section opens on a line of its own, and a sentence
 * carries on along it. Without that, each recital flashed a card until it closed.
 */
const codeSpanPattern = /\[\/?START_CODE\]([\s\S]*?)(\[\/?END_CODE\]|(?=\[\/?START_[A-Z0-9_]+\])|$)/gi;

function unmarkMentionedCodeSpans(content: string): string {
  return content.replace(codeSpanPattern, (span: string, inner: string, close: string, offset: number) => {
    if (looksLikePlcCode(inner)) return span;
    const stillArriving = !close && offset + span.length === content.length;
    if (stillArriving && /^[ \t]*(?:\r?\n|$)/.test(inner)) return span;
    return inner;
  });
}

export function normalizeStructuredMarkers(content: string | undefined | null): string {
  const canonical = toText(content)
    .replace(emphasisedMarkerPattern, '$2')
    .split(/\r?\n/)
    .map((line) => {
      const startEnd = line.match(bareStartEndPattern);
      if (startEnd) {
        const tag = canonicalStructuredTag(startEnd[2]);
        if (tag) return `[${startEnd[1].toUpperCase()}_${tag}]`;
      }

      const section = line.match(bareSectionPattern);
      if (section) {
        const tag = canonicalStructuredTag(section[1]);
        if (tag) return `[START_${tag}]`;
      }

      return line;
    })
    .join('\n');
  return unmarkMentionedCodeSpans(canonical);
}

function hasStructuredStart(content: string): boolean {
  return structuredStartPattern.test(normalizeStructuredMarkers(content));
}

export function extractTaggedSection(content: string | undefined | null, tag: string) {
  const safeContent = normalizeStructuredMarkers(content);
  const nextStartPattern = '\\[/?START_[A-Z0-9_]+\\]';
  const endTagPattern = `\\[/?END_${tag}\\]`;
  const regex = new RegExp(
    `\\[/?START_${tag}\\]([\\s\\S]*?)(?:${endTagPattern}|${nextStartPattern}|${streamErrorBoundaryPattern}|$)`,
    'i',
  );
  const match = safeContent.match(regex);
  return stripStructuredTags(match?.[1] || '');
}

/**
 * Any fence label: models write ```st, ```iecst, ```structured text, ```iec-st.
 * Listing labels left every other one inside the code shown to the engineer.
 */
export function stripMarkdownFence(content: string | undefined | null) {
  const safeContent = toText(content);
  const match = safeContent.match(/^```[^`\n]*\n([\s\S]*?)\n?```$/);
  return (match ? match[1] : safeContent).trim();
}

export function stripStructuredTags(content: string | undefined | null) {
  return normalizeStructuredMarkers(content)
    .replace(structuredTagPattern, '')
    .replace(bareStartEndTokenPattern, '')
    .trim();
}

/**
 * Every `[START_CODE]` section that holds a program, in the order the model
 * wrote them.
 *
 * The vendor rules ask for a FUNCTION_BLOCK with a clean interface *and* a
 * PROGRAM that instantiates it and wires the I/O to its pins, so a model that
 * answers in two sections is answering correctly. Only the first was ever kept,
 * while the body drops *every* section once the turn settles — so a program
 * delivered second was on screen while it streamed and then gone for good: not
 * in the code card, not in the file list, not downloadable, and not the source
 * CODESYS compiled. Splitting the joined text back into one artifact per POU is
 * already `extractPLCBlocks`'s job, so nothing downstream has to change.
 *
 * The span ends at a lookahead rather than at a consumed terminator: sections
 * written back to back with no `[END_CODE]` between them are routine, and
 * consuming the marker that closes one would swallow the next one's opening.
 */
const codeSectionPattern = new RegExp(
  `\\[/?START_CODE\\]([\\s\\S]*?)(?=\\[/?END_CODE\\]|\\[/?START_[A-Z0-9_]+\\]|${streamErrorBoundaryPattern}|$)`,
  'gi',
);

function extractTaggedProgram(content: string): string {
  const sections: string[] = [];
  for (const match of content.matchAll(codeSectionPattern)) {
    const section = stripMarkdownFence(stripStructuredTags(match[1] || ''));
    // A section is the program only when it reads as one. The span still
    // arriving that `unmarkMentionedCodeSpans` lets through was judged by where
    // its marker stands, and that says nothing about what follows it.
    if (!looksLikePlcCode(section)) continue;
    // A section the model wrote out twice is one program, not two.
    if (!sections.includes(section)) sections.push(section);
  }
  return sections.join('\n\n');
}

export function extractCleanStructuredCode(content: string | undefined | null): string {
  const safeContent = normalizeStructuredMarkers(content);
  const taggedCode = extractTaggedProgram(safeContent);
  if (taggedCode) return taggedCode;
  return extractFallbackPlcCode(safeContent);
}

export function extractFallbackPlcCode(content: string | undefined | null): string {
  const safeContent = stripStructuredTags(content);
  if (!safeContent.trim()) return '';

  const fencePattern = /```(?:st|scl|iecst|pascal|structured\s*text|lad)?\s*\n([\s\S]*?)```/gi;
  let match: RegExpExecArray | null;
  while ((match = fencePattern.exec(safeContent)) !== null) {
    const candidate = (match[1] || '').trim();
    if (looksLikePlcCode(candidate)) {
      return candidate;
    }
  }

  // No fence: the whole reply is the candidate, so it has to *be* a program,
  // not merely contain a line of one.
  const cleaned = stripMarkdownFence(safeContent);
  return isMostlyPlcCode(cleaned) ? cleaned : '';
}

function looksLikeDiagnosticOrExampleSnippet(content: string): boolean {
  const normalized = stripStructuredTags(content).replace(/\s+/g, ' ').trim();
  if (!normalized) return false;

  return /(?:检查|校验|验证|错误|问题|诊断|分析|片段|示例|例子|参考|原始程序|原程序|待检查|check|validate|verify|verification|error|issue|diagnos|analysis|snippet|example|sample|reference|original\s+program)/i.test(normalized);
}

export function isInternalStatusExplanation(content: string | undefined | null): boolean {
  const normalized = stripStructuredTags(content)
    .replace(/^>\s*/gm, '')
    .trim();

  if (!normalized) {
    return false;
  }

  return (
    /Engineering planning was skipped due to an error/i.test(normalized)
    || /The code will be generated without a structured plan/i.test(normalized)
  );
}

export function sanitizeReasoningContent(content: string | undefined | null) {
  const safeContent = normalizeStructuredMarkers(content);
  if (!safeContent.trim()) {
    return '';
  }

  const extractedThought = extractTaggedSection(safeContent, 'THOUGHT');
  let reasoning = extractedThought || safeContent;

  reasoning = reasoning
    .replace(/\[\/?END_THOUGHT\][\s\S]*$/i, '')
    .replace(/\[\/?START_(CODE|EXPLANATION|VERIFICATION)\][\s\S]*$/i, '')
    .replace(/\b(?:START|END)[_\s-]*(CODE|EXPLANATION|VERIFICATION|SUMMARY|FLOWCHART)\b[\s\S]*$/i, '')
    // Strip markdown code fences and their content from reasoning to prevent
    // code or verification content from leaking into the reasoning panel
    .replace(/```[\s\S]*?```/g, '');

  // Show the thinking, not the prompt it was given. A thinking model quotes its
  // instructions back at itself, so the panel filled with our own scaffolding —
  // the numbered topology enum, the schema's field names, the headings
  // retrieveContext builds, the line telling it to go and write the JSON.
  return stripInternalScaffolding(stripStructuredTags(reasoning));
}

export function hideIncompleteStreamingMermaid(content: string | undefined | null, isStreaming: boolean) {
  const safeContent = toText(content);
  if (!isStreaming) {
    return safeContent;
  }

  mermaidFenceStartPattern.lastIndex = 0;

  let lastOpen: { index: number; afterOpen: number; fenceChar: string; fenceLength: number } | undefined;
  let match: RegExpExecArray | null;

  while ((match = mermaidFenceStartPattern.exec(safeContent)) !== null) {
    const fence = match[2] || '';
    lastOpen = {
      index: match.index,
      afterOpen: mermaidFenceStartPattern.lastIndex,
      fenceChar: fence.charAt(0),
      fenceLength: fence.length,
    };
  }

  if (!lastOpen) {
    return safeContent;
  }

  const closingFencePattern = new RegExp(`^\\s*${lastOpen.fenceChar}{${lastOpen.fenceLength},}\\s*$`, 'm');
  if (closingFencePattern.test(safeContent.slice(lastOpen.afterOpen))) {
    return safeContent;
  }

  return safeContent.slice(0, lastOpen.index).trimEnd();
}

/** Generic filler the model is told not to emit; dropped if it slips through. */
const GENERIC_FOLLOW_UP_PATTERN = /^(优化(这段)?代码|添加错误处理(逻辑)?|生成(对应的)?测试用例|重新生成|换个方式实现|解释这段逻辑|optimi[sz]e (this )?code|add error handling|generate test cases|regenerate|try a different approach|explain this logic)[。.!！]?$/i;

/**
 * Turn the [START_FOLLOWUP] block into clickable suggestions.
 * One per line; strips bullets/numbering, drops generic filler, caps at 4.
 */
export function parseFollowUpSection(section: string | undefined | null): string[] | undefined {
  if (!section) return undefined;

  const items = section
    .split(/\r?\n/)
    .map(line => line.replace(/^\s*(?:[-*•]|\d+[.)、])\s*/, '').trim())
    .filter(line => line.length > 0 && line.length <= 60)
    .filter(line => !GENERIC_FOLLOW_UP_PATTERN.test(line));

  const unique = [...new Set(items)].slice(0, 4);
  return unique.length > 0 ? unique : undefined;
}

export function parseStreamedContent(content: string | undefined | null, reasoning = ''): GeneratedResult | undefined {
  const safeContent = normalizeStructuredMarkers(content);
  const sanitizedReasoning = sanitizeReasoningContent(reasoning);
  const hasStructuredSections = hasStructuredStart(safeContent);
  const fallbackCode = hasStructuredSections || !looksLikeDiagnosticOrExampleSnippet(safeContent)
    ? extractFallbackPlcCode(safeContent)
    : '';

  if (!hasStructuredSections && !fallbackCode) {
    return undefined;
  }

  const thought = extractTaggedSection(safeContent, 'THOUGHT') || sanitizedReasoning;
  const code = hasStructuredSections ? extractCleanStructuredCode(safeContent) : fallbackCode;
  const explanation = extractTaggedSection(safeContent, 'EXPLANATION');
  const flowchart = extractTaggedSection(safeContent, 'FLOWCHART') || undefined;
  const summary = extractTaggedSection(safeContent, 'SUMMARY') || undefined;
  const clarify = extractTaggedSection(safeContent, 'CLARIFY') || undefined;
  const verification = extractTaggedSection(safeContent, 'VERIFICATION') || undefined;
  const faults = extractTaggedSection(safeContent, 'FAULTS') || undefined;
  const openQuestions = extractTaggedSection(safeContent, 'QUESTIONS') || undefined;
  const risks = extractTaggedSection(safeContent, 'RISKS') || undefined;
  const followUps = parseFollowUpSection(extractTaggedSection(safeContent, 'FOLLOWUP'));

  if (!thought && !code && !explanation && !flowchart && !summary && !clarify && !verification
    && !faults && !openQuestions && !risks) {
    return undefined;
  }

  return {
    code,
    explanation,
    flowchart,
    summary,
    thought,
    clarify,
    verification,
    faults,
    openQuestions,
    risks,
    followUps,
    timestamp: Date.now(),
  };
}
